import { nextResetAt, type RoomMode } from '@os/shared';

export const ROOM_MODES: readonly RoomMode[] = ['normal', 'economy', 'edits', 'suspended'];

/** A suspended tab reconnects this long after the room's reset, at random: not every tab at 00:00:00 (P12). */
const RESUME_MIN_MS = 30_000;
const RESUME_SPREAD_MS = 90_000;
/** A `resetAt` further than this is not believed (the next 00:00 UTC is at most a day away). */
const DAY_MS = 86_400_000;

/**
 * What presence does in a quota mode (spec §18.4): all of it when normal; cursors at 2 Hz and no viewport for
 * followers in economy; nothing from 90 % (the room drops it anyway), so only edits use the quota.
 */
export function presenceIn(mode: RoomMode): { on: boolean; economy: boolean } {
  return { on: mode === 'normal' || mode === 'economy', economy: mode === 'economy' };
}

/** The room's `{type:'mode'}` message, or null when it is not one this build understands. */
export function parseMode(msg: { type?: unknown; mode?: unknown; resetAt?: unknown }): { mode: RoomMode; resetAt: string | null } | null {
  if (msg.type !== 'mode' || !ROOM_MODES.includes(msg.mode as RoomMode)) return null;
  const resetAt = typeof msg.resetAt === 'string' && Number.isFinite(Date.parse(msg.resetAt)) ? msg.resetAt : null;
  return { mode: msg.mode as RoomMode, resetAt };
}

/**
 * When a tab suspended by the room (4503) may reconnect: the room's reset plus 30 to 120 s. A reset already past by
 * this tab's clock (its clock is ahead, or the room has not seen the new day yet) means another try in 30 to 120 s;
 * a missing or far-off one falls back to the next 00:00 UTC by this clock.
 */
export function resumeAt(resetAt: string | null, now: number, random: () => number = Math.random): number {
  const jitter = RESUME_MIN_MS + Math.floor(random() * RESUME_SPREAD_MS);
  const reset = resetAt === null ? NaN : Date.parse(resetAt);
  if (!Number.isFinite(reset) || reset > now + DAY_MS) return nextResetAt(new Date(now)).getTime() + jitter;
  return Math.max(reset, now) + jitter;
}
